import {
  labelBarcodePrintHeightDots,
  LABEL_BARCODE_TO_BOTTOM_GAP_MM,
  LABEL_BOTTOM_LINE_HEIGHT_MM,
  LABEL_META_HEIGHT_MM,
  LABEL_META_TO_BAR_GAP_MM,
  LABEL_PANEL_PAD_MM,
  LABEL_TOP_PAD_MM,
} from './inventoryLabelBarcode';
import {
  LABEL_BARCODE_LEFT_MM,
  LABEL_INFO_LEFT_MM,
  LABEL_PRINT_GAP_MM,
  LABEL_PRINT_HEIGHT_MM,
  LABEL_PRINT_WIDTH_MM,
  mmToDots,
} from './inventoryLabelGeometry';

export interface InventoryLabelEplRow {
  productCodeUnique: string;
  materialLabel: string;
  salePrice: number;
  weightGrams: number | null;
  diameterSize?: string | null;
  workshopCode?: string | null;
}

function eplText(value: string, max = 40): string {
  return value
    .replace(/[\r\n"]/g, ' ')
    .trim()
    .slice(0, max);
}

function formatPriceBs(price: number): string {
  const n = Number(price);
  const s = n.toLocaleString('es-BO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `Bs. ${s}`;
}

function formatWeight(weightGrams: number | null): string {
  if (weightGrams == null || Number.isNaN(Number(weightGrams))) return '';
  return Number(weightGrams).toLocaleString('es-BO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/** Placeholder when a label has no material text; an empty panel reads as a printing fault. */
const EPL_MATERIAL_FALLBACK = '-';
/** Approximate advance of EPL font 1 in dots, used to right-align text inside a panel. */
const EPL_GLYPH_DOTS = 8;
const EPL_BOTTOM_GAP_DOTS = 6;
const EPL_PANEL_RIGHT_MARGIN_DOTS = 4;
const EPL_BARCODE_NARROW_DOTS = 1;
const EPL_BARCODE_WIDE_DOTS = 2;
/** Pack from the top; bottom line is pinned to the strip edge (8 dots/mm). */
export const EPL_META_Y = mmToDots(LABEL_TOP_PAD_MM);
export const EPL_BARCODE_Y = mmToDots(
  LABEL_TOP_PAD_MM + LABEL_META_HEIGHT_MM + LABEL_META_TO_BAR_GAP_MM,
);
/** Clearance between bars and código proveedor / talla. */
export const EPL_BOTTOM_LINE_OFFSET_DOTS = mmToDots(LABEL_BARCODE_TO_BOTTOM_GAP_MM);

/** Y of the código proveedor / talla line, pinned to the bottom of the strip. */
export function eplBottomLineY(labelHeightDots: number): number {
  return labelHeightDots - mmToDots(LABEL_PANEL_PAD_MM + LABEL_BOTTOM_LINE_HEIGHT_MM);
}

/** EPL: empty 44 mm neck + Plata/Bs panel + barcode panel flush with the strip end. */
export function buildSingleLabelEpl(row: InventoryLabelEplRow): string {
  const labelWidthDots = mmToDots(LABEL_PRINT_WIDTH_MM);
  const labelHeightDots = mmToDots(LABEL_PRINT_HEIGHT_MM);
  const labelGapDots = mmToDots(LABEL_PRINT_GAP_MM);
  const barX = mmToDots(LABEL_BARCODE_LEFT_MM);
  const infoX = mmToDots(LABEL_INFO_LEFT_MM);

  const material = eplText(row.materialLabel ?? '');
  const price = eplText(formatPriceBs(row.salePrice));
  const weight = eplText(formatWeight(row.weightGrams));
  const code = eplText(row.productCodeUnique ?? '');
  const workshop = eplText(row.workshopCode ?? '', 22);
  const talla = eplText(row.diameterSize ?? '', 12);
  // The bottom band is always reserved, so the bars stay the same height either way.
  const hasBottomLine = workshop !== '' || talla !== '';
  const bottomLineY = eplBottomLineY(labelHeightDots);
  const barHeightDots = Math.min(
    labelBarcodePrintHeightDots(true),
    bottomLineY - EPL_BARCODE_Y - EPL_BOTTOM_LINE_OFFSET_DOTS,
  );

  /** EPL has no alignment, so right edges are computed from the strip end. */
  const rightAlignedX = (text: string): number =>
    labelWidthDots - text.length * EPL_GLYPH_DOTS - EPL_PANEL_RIGHT_MARGIN_DOTS;

  const lines = ['N', `q${labelWidthDots}`, `Q${labelHeightDots},${labelGapDots}`];
  if (code !== '') {
    lines.push(`A${barX},${EPL_META_Y},0,1,1,1,N,"${code}"`);
    lines.push(
      `B${barX},${EPL_BARCODE_Y},0,1A,${EPL_BARCODE_NARROW_DOTS},${EPL_BARCODE_WIDE_DOTS},${barHeightDots},N,"${code}"`,
    );
  }
  if (weight !== '') lines.push(`A${rightAlignedX(weight)},${EPL_META_Y},0,1,1,1,N,"${weight}"`);
  lines.push(`A${infoX},12,0,2,1,1,N,"${material === '' ? EPL_MATERIAL_FALLBACK : material}"`);
  if (price !== '') lines.push(`A${infoX},42,0,2,1,1,N,"${price}"`);
  if (hasBottomLine) {
    const tallaX = rightAlignedX(talla);
    if (workshop !== '') {
      // EPL cannot clip, so truncate instead: overprinting the talla is worse than a short code.
      const availableDots = tallaX - EPL_BOTTOM_GAP_DOTS - barX;
      const maxChars = Math.max(0, Math.floor(availableDots / EPL_GLYPH_DOTS));
      const fitted = workshop.slice(0, maxChars);
      if (fitted !== '') lines.push(`A${barX},${bottomLineY},0,1,1,1,N,"${fitted}"`);
    }
    if (talla !== '') lines.push(`A${tallaX},${bottomLineY},0,1,1,1,N,"${talla}"`);
  }
  lines.push('P1', '');

  return lines.join('\n');
}

export function buildInventoryLabelsEpl(rows: InventoryLabelEplRow[]): string {
  return rows.map(buildSingleLabelEpl).join('');
}
